import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  HostListener,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OperationTableau } from '../../../models/operation-planifiee.model';
import { OperationReelle } from '../../../models/operation-reelle.model';

export interface LigneImportee {
  dateStr: string;
  date: Date;
  dateValeurStr: string | null;
  dateValeur: Date | null;
  libelle: string;
  lieu: string;
  categorie: string;
  typeLabel: string;
  statut: string;
  montantBrut: number;
  typeOp: 'ENTREE' | 'SORTIE';
  moyenPaiementDetecte: string | null;
  numeroCheque: string | null;
  referenceImport: string;
}

export interface AssociationConfirmee {
  ligne: LigneImportee;
  operationId: number | null;
  compteDestinationId: number | null;
  referenceImport: string;
  creerNouvelleOp?: true;
}

interface AssociationRow {
  ligne: LigneImportee;
  operationSelectionneeId: number | null;
  scoreConfiance: number;
  ignore: boolean;
  dejaTraitee: boolean;
  opReelleExistante: OperationReelle | null;
  creerNouvelleOp: boolean;
}

interface TypeDisponible {
  cle: string;
  libelle: string;
  nombre: number;
  selectionnables: number;
  selectionnees: number;
}

type ColonneTri =
  | 'SELECTION'
  | 'DATE'
  | 'DATE_VALEUR'
  | 'LIBELLE'
  | 'TYPE'
  | 'MONTANT'
  | 'ASSOCIATION'
  | 'CONFIANCE';
type SensTri = 'ASC' | 'DESC';

@Component({
    selector: 'app-popup-import-planifiee',
    imports: [CommonModule, FormsModule],
    templateUrl: './popup-import-planifiee.component.html',
    styleUrl: './popup-import-planifiee.component.scss'
})
export class PopupImportPlanifieeComponent implements OnChanges {
  @ViewChild('selecteurTypes') selecteurTypes?: ElementRef<HTMLDetailsElement>;

  @Input() visible = false;
  @Input() source = 'presse-papier';
  @Input() lignes: LigneImportee[] = [];
  @Input() toutesLignes: LigneImportee[] = [];
  @Input() operations: OperationTableau[] = [];
  @Input() tableauMois: { annee: number; mois: number }[] = [];

  @Output() confirmer = new EventEmitter<AssociationConfirmee[]>();
  @Output() annuler = new EventEmitter<void>();

  rows: AssociationRow[] = [];
  typesDisponibles: TypeDisponible[] = [];
  afficherDejaTraitees = false;
  colonneTri: ColonneTri | null = null;
  sensTri: SensTri = 'ASC';

  estOperationAVenir(ligne: LigneImportee): boolean {
    return ligne.statut.trim().toLocaleLowerCase('fr-FR') === 'à venir';
  }

  @HostListener('document:click', ['$event'])
  fermerSelecteurTypesAuClicExterieur(event: MouseEvent): void {
    const selecteur = this.selecteurTypes?.nativeElement;
    const cible = event.target as Node | null;
    if (selecteur?.open && cible && !selecteur.contains(cible)) {
      selecteur.open = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.visible && changes['visible']) {
      this.colonneTri = null;
      this.sensTri = 'ASC';
    }

    if (this.visible && (changes['visible'] || changes['lignes'] || changes['operations'])) {
      const toutesReelles = this.operations.flatMap((op) => op.operationsReelles ?? []);
      const idsDejaAttribues = new Set<number>();

      this.rows = this.lignes.map((ligne) => {
        const { operationId, score } = this.suggerer(ligne);
        const opReelleExistante = this.trouverReelleExistante(ligne, toutesReelles);
        const dejaTraitee = opReelleExistante !== null;

        const opSuggereeProv = operationId !== null && this.operations.find(
          (o) => o.id === operationId || o.echeancesParMois?.some((e) => e?.id === operationId)
        )?.provision;
        const opIdEffectif = operationId !== null && !opSuggereeProv && idsDejaAttribues.has(operationId) ? null : operationId;
        if (opIdEffectif !== null && !dejaTraitee && !opSuggereeProv) {
          idsDejaAttribues.add(opIdEffectif);
        }

        return {
          ligne,
          operationSelectionneeId: dejaTraitee ? null : opIdEffectif,
          scoreConfiance: score,
          ignore: dejaTraitee || this.estOperationAVenir(ligne),
          dejaTraitee,
          opReelleExistante,
          creerNouvelleOp: false,
        };
      });
      this.recalculerTypesDisponibles();
    }
  }

  private moisIdxPourLigne(ligne: LigneImportee): number {
    const annee = ligne.date.getFullYear();
    const mois = ligne.date.getMonth() + 1;
    return this.tableauMois.findIndex((m) => m.annee === annee && m.mois === mois);
  }

  opsCompatibles(ligne: LigneImportee): OperationTableau[] {
    const idx = this.moisIdxPourLigne(ligne);
    return this.operations.filter((op) =>
      !op.estReelle && op.type === ligne.typeOp &&
      // Un budget épuisé ou une périodicité décalée ne bloque pas une provision,
      // mais une provision dont la durée est terminée n'est plus proposée.
      (op.provision
        ? !this.provisionTerminee(op)
        : idx >= 0 && (op.montantParMois[idx] ?? 0) > 0),
    );
  }

  private provisionTerminee(op: OperationTableau): boolean {
    if (!op.nombreDeMois || op.nombreDeMois <= 0) return false;
    const [anneeDebut, moisDebut] = op.dateDebut.split('-').map(Number);
    const moisFin = anneeDebut * 12 + moisDebut - 1 + op.nombreDeMois;
    const maintenant = new Date();
    const moisCourant = maintenant.getFullYear() * 12 + maintenant.getMonth();
    // Comparaison au mois courant, même pour l'import d'un paiement plus ancien.
    return moisFin < moisCourant;
  }

  montantOpCeMois(op: OperationTableau, ligne: LigneImportee): number {
    const idx = this.moisIdxPourLigne(ligne);
    return idx >= 0 ? (op.montantParMois[idx] ?? 0) : 0;
  }

  /** Retourne l'ID réel à utiliser pour pointer : mensualité active si applicable, sinon op.id */
  effectiveOperationId(op: OperationTableau, ligne: LigneImportee): number {
    const idx = this.moisIdxPourLigne(ligne);
    if (idx >= 0 && op.echeancesParMois?.[idx]?.id) {
      return op.echeancesParMois[idx]!.id;
    }
    return op.id;
  }

  get rowsVisibles(): AssociationRow[] {
    const rows = this.afficherDejaTraitees
      ? [...this.rows]
      : this.rows.filter((r) => !r.dejaTraitee);

    if (this.colonneTri) {
      const direction = this.sensTri === 'ASC' ? 1 : -1;
      rows.sort((a, b) => direction * this.comparerRows(a, b, this.colonneTri!));
    }

    return rows;
  }

  private recalculerTypesDisponibles(): void {
    const types = new Map<string, {
      libelle: string;
      nombre: number;
      selectionnables: number;
      selectionnees: number;
    }>();
    for (const row of this.rows) {
      const cle = this.cleType(row.ligne);
      const type = types.get(cle);
      if (type) {
        type.nombre += 1;
        if (!row.dejaTraitee) type.selectionnables += 1;
        if (!row.dejaTraitee && !row.ignore) type.selectionnees += 1;
      } else {
        types.set(cle, {
          libelle: this.libelleType(row.ligne),
          nombre: 1,
          selectionnables: row.dejaTraitee ? 0 : 1,
          selectionnees: !row.dejaTraitee && !row.ignore ? 1 : 0,
        });
      }
    }
    this.typesDisponibles = [...types.entries()]
      .map(([cle, type]) => ({ cle, ...type }))
      .sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr', { sensitivity: 'base' }));
  }

  get nbTypesAvecSelection(): number {
    return this.typesDisponibles.filter((type) => type.selectionnees > 0).length;
  }

  get nbLignesModifiablesVisibles(): number {
    return this.rowsVisibles.filter((row) => !row.dejaTraitee).length;
  }

  get nbLignesSelectionneesVisibles(): number {
    return this.rowsVisibles.filter((row) => !row.dejaTraitee && !row.ignore).length;
  }

  definirLigneSelectionnee(row: AssociationRow, selectionnee: boolean): void {
    this.appliquerSelectionLigne(row, selectionnee);
    this.recalculerTypesDisponibles();
  }

  private appliquerSelectionLigne(row: AssociationRow, selectionnee: boolean): void {
    if (row.dejaTraitee) return;
    row.ignore = !selectionnee;
    if (
      selectionnee &&
      row.operationSelectionneeId !== null &&
      this.isOpDejaSelectionnee(row.operationSelectionneeId, row)
    ) {
      row.operationSelectionneeId = null;
    }
  }

  selectionnerLignesVisibles(selectionnees: boolean): void {
    for (const row of this.rowsVisibles) {
      this.appliquerSelectionLigne(row, selectionnees);
    }
    this.recalculerTypesDisponibles();
  }

  selectionnerType(cleType: string, selectionnees: boolean): void {
    for (const row of this.rows) {
      if (this.cleType(row.ligne) === cleType) {
        this.appliquerSelectionLigne(row, selectionnees);
      }
    }
    this.recalculerTypesDisponibles();
  }

  trackByType(_: number, type: TypeDisponible): string {
    return type.cle;
  }

  trierPar(colonne: ColonneTri): void {
    if (this.colonneTri === colonne) {
      this.sensTri = this.sensTri === 'ASC' ? 'DESC' : 'ASC';
      return;
    }
    this.colonneTri = colonne;
    this.sensTri = 'ASC';
  }

  indicateurTri(colonne: ColonneTri): string {
    if (this.colonneTri !== colonne) return '';
    return this.sensTri === 'ASC' ? '▲' : '▼';
  }

  ariaTri(colonne: ColonneTri): 'ascending' | 'descending' | null {
    if (this.colonneTri !== colonne) return null;
    return this.sensTri === 'ASC' ? 'ascending' : 'descending';
  }

  private cleType(ligne: LigneImportee): string {
    return ligne.typeLabel.trim().toLocaleLowerCase('fr-FR') || '__sans_type__';
  }

  private libelleType(ligne: LigneImportee): string {
    return ligne.typeLabel.trim() || 'Sans type';
  }

  private comparerRows(
    a: AssociationRow,
    b: AssociationRow,
    colonne: ColonneTri,
  ): number {
    switch (colonne) {
      case 'SELECTION':
        return Number(a.ignore) - Number(b.ignore);
      case 'DATE':
        return a.ligne.date.getTime() - b.ligne.date.getTime();
      case 'DATE_VALEUR':
        return (a.ligne.dateValeur?.getTime() ?? 0) - (b.ligne.dateValeur?.getTime() ?? 0);
      case 'LIBELLE':
        return this.comparerTexte(a.ligne.libelle, b.ligne.libelle);
      case 'TYPE':
        return this.comparerTexte(this.libelleType(a.ligne), this.libelleType(b.ligne));
      case 'MONTANT':
        return a.ligne.montantBrut - b.ligne.montantBrut;
      case 'ASSOCIATION':
        return this.comparerTexte(this.libelleAssociation(a), this.libelleAssociation(b));
      case 'CONFIANCE':
        return a.scoreConfiance - b.scoreConfiance;
    }
  }

  private comparerTexte(a: string, b: string): number {
    return a.localeCompare(b, 'fr', { sensitivity: 'base' });
  }

  private libelleAssociation(row: AssociationRow): string {
    if (row.creerNouvelleOp) return 'Nouvelle opération réelle';
    if (row.operationSelectionneeId === null) return '';
    return this.operations.find(
      (op) => op.id === row.operationSelectionneeId ||
        op.echeancesParMois?.some((echeance) => echeance?.id === row.operationSelectionneeId),
    )?.libelle ?? '';
  }

  get nbAssociations(): number {
    return this.rows.filter((r) => !r.ignore && !r.dejaTraitee && (r.operationSelectionneeId !== null || r.creerNouvelleOp)).length;
  }

  get nbDejaTraitees(): number {
    return this.rows.filter((r) => r.dejaTraitee).length;
  }

  isOpDejaSelectionnee(effectiveOpId: number, currentRow: AssociationRow): boolean {
    // Les provisions sont un pool réutilisable : jamais bloquées
    const op = this.operations.find(
      (o) => o.id === effectiveOpId || o.echeancesParMois?.some((e) => e?.id === effectiveOpId),
    );
    if (op?.provision) return false;
    return this.rows.some(
      (r) => r !== currentRow && !r.ignore && !r.dejaTraitee && r.operationSelectionneeId === effectiveOpId,
    );
  }

  private trouverReelleExistante(
    ligne: LigneImportee,
    toutesReelles: OperationReelle[],
  ): OperationReelle | null {
    if (!ligne.referenceImport) return null;
    return toutesReelles.find((r) => r.referenceImport != null && r.referenceImport === ligne.referenceImport) ?? null;
  }

  formatterMontant(montant: number): string {
    const abs = Math.abs(montant);
    const sign = montant < 0 ? '−\u202f' : '+\u202f';
    return `${sign}${abs.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\u00a0€`;
  }

  labelConfiance(score: number): 'haute' | 'moyenne' | 'faible' {
    if (score >= 50) return 'haute';
    if (score >= 20) return 'moyenne';
    return 'faible';
  }

  private suggerer(ligne: LigneImportee): { operationId: number | null; score: number } {
    const compat = this.opsCompatibles(ligne);
    let bestId: number | null = null;
    let bestScore = 0;

    for (const op of compat) {
      let score = 0;
      const montantOp = this.montantOpCeMois(op, ligne);
      const montantImport = Math.abs(ligne.montantBrut);

      if (montantOp > 0) {
        const pct = Math.abs(montantOp - montantImport) / montantOp;
        if (pct <= 0.01) score += 50;
        else if (pct <= 0.05) score += 20;
        else if (pct <= 0.15) score += 5;
      }

      const jourImport = ligne.date.getDate();
      const diffJour = Math.abs(jourImport - op.jourDuMois);
      if (diffJour === 0) score += 20;
      else if (diffJour <= 3) score += 10;
      else if (diffJour <= 7) score += 3;

      const motsImport = ligne.libelle
        .toLowerCase()
        .split(/[\s,.\-_/]+/)
        .filter((m) => m.length > 2);
      const motsPlan = op.libelle
        .toLowerCase()
        .split(/[\s,.\-_/]+/)
        .filter((m) => m.length > 2);
      for (const mot of motsImport) {
        if (motsPlan.some((p) => p.includes(mot) || mot.includes(p))) {
          score += 5;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestId = this.effectiveOperationId(op, ligne);
      }
    }

    return { operationId: bestId, score: bestScore };
  }

  basculerCreation(row: AssociationRow): void {
    row.creerNouvelleOp = !row.creerNouvelleOp;
    if (row.creerNouvelleOp) {
      row.ignore = false;
      row.operationSelectionneeId = null;
    }
    this.recalculerTypesDisponibles();
  }

  get nbLignesMoisPrecedentDisponibles(): number {
    return this.lignesMoisPrecedentDisponibles().length;
  }

  chargerMoisPrecedent(): void {
    const nouvellesLignes = this.lignesMoisPrecedentDisponibles();
    if (nouvellesLignes.length === 0) return;

    const toutesReelles = this.operations.flatMap((op) => op.operationsReelles ?? []);
    const idsDejaAttribues = new Set(
      this.rows
        .filter((r) => !r.ignore && !r.dejaTraitee && r.operationSelectionneeId !== null)
        .map((r) => r.operationSelectionneeId!),
    );

    for (const ligne of nouvellesLignes) {
      const { operationId, score } = this.suggerer(ligne);
      const opReelleExistante = this.trouverReelleExistante(ligne, toutesReelles);
      const dejaTraitee = opReelleExistante !== null;

      const opSuggereeProv = operationId !== null && this.operations.find(
        (o) => o.id === operationId || o.echeancesParMois?.some((e) => e?.id === operationId)
      )?.provision;
      const opIdEffectif = operationId !== null && !opSuggereeProv && idsDejaAttribues.has(operationId) ? null : operationId;
      if (opIdEffectif !== null && !dejaTraitee && !opSuggereeProv) {
        idsDejaAttribues.add(opIdEffectif);
      }
      
      this.rows.push({
        ligne,
        operationSelectionneeId: dejaTraitee ? null : opIdEffectif,
        scoreConfiance: score,
        ignore: dejaTraitee || this.estOperationAVenir(ligne),
        dejaTraitee,
        opReelleExistante,
        creerNouvelleOp: false,
      });
    }
    this.recalculerTypesDisponibles();
  }

  private lignesMoisPrecedentDisponibles(): LigneImportee[] {
    const cible = this.moisPrecedentCible();
    if (!cible) return [];

    const referencesDejaAffichees = new Set(this.rows.map((r) => r.ligne.referenceImport));
    return this.toutesLignes.filter(
      (l) =>
        l.date.getFullYear() === cible.annee &&
        l.date.getMonth() + 1 === cible.mois &&
        !referencesDejaAffichees.has(l.referenceImport),
    );
  }

  private moisPrecedentCible(): { annee: number; mois: number } | null {
    const reference = this.lignes[0]?.date ?? this.rows[0]?.ligne.date;
    if (!reference) return null;

    const moisPrecedent = new Date(reference);
    moisPrecedent.setDate(1);
    moisPrecedent.setMonth(moisPrecedent.getMonth() - 1);
    return {
      annee: moisPrecedent.getFullYear(),
      mois: moisPrecedent.getMonth() + 1,
    };
  }

  valider(): void {
    const associations: AssociationConfirmee[] = this.rows
      .filter((r) => !r.ignore && !r.dejaTraitee && (r.operationSelectionneeId !== null || r.creerNouvelleOp))
      .map((r) => {
        if (r.creerNouvelleOp) {
          return {
            ligne: r.ligne,
            operationId: null,
            compteDestinationId: null,
            referenceImport: r.ligne.referenceImport,
            creerNouvelleOp: true as const,
          };
        }
        const op = this.operations.find((o) => o.id === r.operationSelectionneeId ||
          o.echeancesParMois?.some((e) => e?.id === r.operationSelectionneeId));
        return {
          ligne: r.ligne,
          operationId: r.operationSelectionneeId!,
          compteDestinationId: op?.compteDestinationId ?? null,
          referenceImport: r.ligne.referenceImport,
        };
      });
    this.confirmer.emit(associations);
  }
}
